import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import getFleetNameById from '../../utils/getFleetNameById';
import fixGeoJSONCoordinates from '../../utils/fixGeoJSONCoordinates';
import getBoundsFromPoints from '../../utils/getBoundsFromPoints';
import LinkToGoogleMap from '../../components/shared/LinkToGoogleMap';
import UploadFileButton from './UploadFileButton';
import styles from './AreaInfo.styles';
import { selectSelectedArea } from '../../redux/store/appData/areas';
import { selectFleets } from '../../redux/store/appData/fleets';
import ListItem from '../../components/shared/ListItem';
import { selectAuth } from '../../redux/store/userData/user/selectors';
import { openModal } from '../../redux/store/ui/common';
import { Collapse } from 'antd';
import ListEditItem from '../shared/ListEditItem';
import { renderText } from '../shared/DrawerLayout/renderText';
import MapView from './MapView/MapView';
import { FormattedMessage, useIntl } from 'react-intl';
import { padZero } from '../../utils';
import rehypeRaw from 'rehype-raw';
import Markdown from 'react-markdown';

const getCenterFromGeoJson = (geoJsonData: any) => {
  const geoJson = fixGeoJSONCoordinates(geoJsonData);
  const bounds = getBoundsFromPoints(
    // @ts-ignore
    geoJson.features[0].geometry.coordinates[0]
  );
  const center = {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };

  return `${center.lat?.toString()?.slice(0, 10)},${center.lng
    ?.toString()
    ?.slice(0, 10)}`;
};

const { Panel } = Collapse;

const areaFallBackOpeningHours = [
  {
    dayOfWeekCode: 'MO',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'TU',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'WE',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'TH',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'FR',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'SA',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'SU',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
];

const AreaInfo: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedArea = useSelector(selectSelectedArea);
  const fleets = useSelector(selectFleets);
  const user = useSelector(selectAuth);

  const onArchiveArea = () => {
    dispatch(openModal('ArchiveAreaModal'));
  };

  const onEditOpeningHours = () => {
    dispatch(openModal('OpeningHoursModal'));
  };

  const onEditAreaNote = () => {
    dispatch(openModal('AreaNoteModal'));
  };

  const onEditAreaName = () => {
    dispatch(openModal('updateAreaNameModal'));
  };

  const onEditAreaDescription = () => {
    dispatch(openModal('updateAreaDescriptionModal'));
  };

  const onEditAreaFleetStyle = () => {
    dispatch(openModal('updateAreaFleetStyleModal'));
  };

  const areaOpeningHours =
    selectedArea?.openingHoursPerDay.length > 0
      ? selectedArea?.openingHoursPerDay
      : areaFallBackOpeningHours;

  return (
    <>
      <div className="area-info">
        <ListEditItem
          testid="name"
          label={<FormattedMessage id="fleetadmin.areaEditor.name" />}
          leftItem={<div>{selectedArea?.name}</div>}
          rightItem={
            <img
              className="edit-button"
              src={'/images/drawer/edit.svg'}
              alt="edit"
              onClick={onEditAreaName}
            />
          }
        />
        <ListEditItem
          testid="description"
          label={<FormattedMessage id="fleetadmin.areaEditor.description" />}
          leftItem={renderText(selectedArea?.description, true)}
          rightItem={
            <img
              className="edit-button"
              src={'/images/drawer/edit.svg'}
              alt="edit"
              onClick={onEditAreaDescription}
            />
          }
        />
        <ListItem
          testid="fleet"
          label="Fleet"
          value={getFleetNameById(fleets, selectedArea?.fleetId)}
        />
        <ListEditItem
          testid="fleetStyle"
          label={<FormattedMessage id="fleetadmin.areaEditor.fleetStyle" />}
          leftItem={renderText(selectedArea?.fleetStyle, true)}
          rightItem={
            <img
              className="edit-button"
              src={'/images/drawer/edit.svg'}
              alt="edit"
              onClick={onEditAreaFleetStyle}
            />
          }
        />
        <ListItem
          testid="location"
          label="Location"
          value={
            <LinkToGoogleMap
              position={getCenterFromGeoJson(selectedArea?.geoJSONData)}
            />
          }
        />
        <ListItem
          testid="file"
          label="File"
          value={
            <a href={selectedArea?.kmlFileUrl}>{selectedArea?.kmlFilename}</a>
          }
        />
        <ListItem
          testid="updatedBy"
          label="Updated By"
          value={getFleetNameById(fleets, selectedArea?.lastUpdatedBy)}
        />
        <ListEditItem
          label={renderText(
            intl.formatMessage({
              id: `fleetadmin.areas.note`,
            })
          )}
          leftItem={
            selectedArea?.note ? (
              <div className="markdown-wrapper">
                <Markdown rehypePlugins={[rehypeRaw]}>
                  {intl.formatMessage({
                    id: `${selectedArea.note}`,
                  })}
                </Markdown>
              </div>
            ) : (
              '-'
            )
          }
          onClickEdit={onEditAreaNote}
        />
        {user.permissions?.fleetManager && (
          <div className="button-wrapper">
            <UploadFileButton />
            <div
              data-testid="AreaInfo-archiveAreaBtn"
              className="archive-area-button"
              onClick={onArchiveArea}
            >
              Archive area
            </div>
          </div>
        )}
        <div className="map-container">
          <MapView selectedArea={selectedArea} />
        </div>
        <div className="collapse-container">
          <Collapse bordered={false}>
            <Panel
              header={intl.formatMessage({
                id: `fleetadmin.areas.openingHours`,
              })}
              key={1}
            >
              <ListEditItem
                leftItem={renderText(
                  intl.formatMessage({
                    id: `fleetadmin.areas.openingHours`,
                  })
                )}
                onClickEdit={onEditOpeningHours}
              />
              {areaOpeningHours?.map((day) => (
                <div key={day.dayOfWeekCode} className="opening-hours-wrapper">
                  <span>
                    {intl.formatMessage({
                      id: `fleetadmin.${day.dayOfWeekCode.toLowerCase()}`,
                    })}
                  </span>
                  <span>
                    {day?.closedWholeDay
                      ? intl.formatMessage({
                          id: `closed`,
                        })
                      : `${padZero(day?.openAtHour)}:${padZero(
                          day.openAtMinute
                        )} bis ${padZero(day.closeAtHour)}:${padZero(
                          day.closeAtMinute
                        )} Uhr`}
                  </span>
                </div>
              ))}
            </Panel>
          </Collapse>
        </div>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default AreaInfo;
